import { createContext, ReactNode, useContext, useReducer } from 'react';
import produce from 'immer';

import { EPISODES_SORT_KEY, SHOWS_SORT_KEY, SORT_ORDER } from '@insights_module/constants/defaults';
import { EpisodeSortType } from '@insights_module/types/Episode';
import { ShowsSortType } from '@insights_module/types/Show';
import { unimplemented } from '@utils/commonUtils';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';

type InsightsContextKeys =
  | 'insights.selected_table_type'
  | 'insights.selected_show'
  | 'insights.show_details.selected_garm_category'
  | 'insights.show_details.episode_list_expanded'
  | 'insights.should_show_shows_table_banner'
  | 'insights.persistedShouldShowShowsTableBanner'
  | 'insights.show_details.selected_contextual_category'
  | 'insights.shows_sort'
  | 'insights.episodes_sort'
  | 'insights.selected_episode'
  | 'insights.show_export_transcript_modal'
  | 'insights.is_transcript_download_in_progress'
  | 'insights.episode_search_term'
  | 'insights.should_show_ai_chat'
  | 'insights.show_filters_modal';

const INSIGHTS_CONTEXT_KEYS = {
  SELECTED_TABLE_TYPE: 'insights.selected_table_type' as InsightsContextKeys,
  SELECTED_SHOW: 'insights.selected_show' as InsightsContextKeys,
  SELECTED_GARM_CATEGORY: 'insights.show_details.selected_garm_category' as InsightsContextKeys,
  EPISODE_LIST_EXPANDED: 'insights.show_details.episode_list_expanded' as InsightsContextKeys,
  SHOULD_SHOW_SHOWS_TABLE_BANNER: 'insights.should_show_shows_table_banner' as InsightsContextKeys,
  PERSISTED_SHOULD_SHOW_SHOWS_TABLE_BANNER:
    'insights.persistedShouldShowShowsTableBanner' as InsightsContextKeys,
  SELECTED_CONTEXTUAL_CATEGORY:
    'insights.show_details.selected_contextual_category' as InsightsContextKeys,
  GARM_OVERVIEW_HEIGHT: 'insights.show_details.garm_overview_height' as InsightsContextKeys,
  SHOWS_SORT: 'insights.shows_sort' as InsightsContextKeys,
  EPISODES_SORT: 'insights.episodes_sort' as InsightsContextKeys,
  SELECTED_EPISODE: 'insights.selected_episode' as InsightsContextKeys,
  SHOW_EXPORT_TRANSCRIPT_MODAL: 'insights.show_export_transcript_modal' as InsightsContextKeys,
  IS_TRANSCRIPT_DOWNLOAD_IN_PROGRESS:
    'insights.is_transcript_download_in_progress' as InsightsContextKeys,
  EPISODE_SEARCH_TERM: 'insights.episode_search_term' as InsightsContextKeys,
  SHOULD_SHOW_AI_CHAT: 'insights.should_show_ai_chat' as InsightsContextKeys,
  SHOW_FILTERS_MODAL: 'insights.show_filters_modal' as InsightsContextKeys,
};

type TableType = 'shows' | 'episodes';

export const TABLE_TYPE = {
  SHOWS: 'shows' as TableType,
  EPISODES: 'episodes' as TableType,
};

type Action = {
  type: InsightsContextKeys;
  payload: string | boolean | null | number | EpisodeSortType | ShowsSortType | TableType;
};

export interface IInsightsContext {
  selectedTableType: TableType;
  selectedShow: string;
  selectedGarmCategory: string;
  episodeListExpanded: boolean;
  shouldShowShowsTableBanner: boolean;
  persistedShouldShowShowsTableBanner: boolean | null;
  selectedContextualCategory: string;
  garmOverviewHeight: number;
  showsSort: ShowsSortType;
  episodesSort: EpisodeSortType;
  selectedEpisode: string;
  showExportTranscriptModal: boolean;
  isTranscriptDownloadInProgress: boolean;
  episodeSearchTerm: string;
  shouldShowAIChat: boolean;
  showFiltersModal: boolean;
  setSelectedTableType: (value: TableType) => void;
  setSelectedShow: (showId: string) => void;
  setSelectedGarmCategory: (garmCategory: string) => void;
  setEpisodeListExpanded: (value: boolean | ((prevState: boolean) => boolean)) => void;
  setShouldShowShowsTableBanner: (value: boolean) => void;
  setPersistedShouldShowShowsTableBanner: (value: boolean) => void;
  setSelectedContextualCategory: (value: string) => void;
  setGarmOverviewHeight: (value: number) => void;
  setShowsSort: (params: ShowsSortType) => void;
  setEpisodesSort: (params: EpisodeSortType) => void;
  setSelectedEpisode: (episodeId: string) => void;
  setShowExportTranscriptModal: (value: boolean) => void;
  setIsTranscriptDownloadInProgress: (value: boolean) => void;
  setEpisodeSearchTerm: (value: string) => void;
  setShouldShowAIChat: (value: boolean) => void;
  setShowFiltersModal: (value: boolean) => void;
}

const initialState: IInsightsContext = {
  selectedTableType: 'shows',
  selectedShow: '',
  selectedGarmCategory: '',
  episodeListExpanded: false,
  shouldShowShowsTableBanner: true,
  persistedShouldShowShowsTableBanner: true,
  selectedContextualCategory: 'iab',
  garmOverviewHeight: 0,
  showsSort: {
    sort_field: SHOWS_SORT_KEY.NAME,
    sort_order: SORT_ORDER.ASC,
  },
  episodesSort: {
    sort_field: EPISODES_SORT_KEY.PUBLISHED_DATE,
    sort_order: SORT_ORDER.DESC,
  },
  selectedEpisode: '',
  showExportTranscriptModal: false,
  isTranscriptDownloadInProgress: false,
  episodeSearchTerm: '',
  shouldShowAIChat: false,
  showFiltersModal: false,
  setSelectedTableType: unimplemented,
  setShowsSort: unimplemented,
  setEpisodesSort: unimplemented,
  setSelectedShow: unimplemented,
  setSelectedGarmCategory: unimplemented,
  setEpisodeListExpanded: unimplemented,
  setShouldShowShowsTableBanner: unimplemented,
  setPersistedShouldShowShowsTableBanner: unimplemented,
  setSelectedContextualCategory: unimplemented,
  setGarmOverviewHeight: unimplemented,
  setSelectedEpisode: unimplemented,
  setShowExportTranscriptModal: unimplemented,
  setIsTranscriptDownloadInProgress: unimplemented,
  setEpisodeSearchTerm: unimplemented,
  setShouldShowAIChat: unimplemented,
  setShowFiltersModal: unimplemented,
};

const localShouldShowShowsTableBanner = localStorage.getItem('show-shows-table-banner');
if (localShouldShowShowsTableBanner) {
  initialState.persistedShouldShowShowsTableBanner = JSON.parse(localShouldShowShowsTableBanner);
  initialState.shouldShowShowsTableBanner = JSON.parse(localShouldShowShowsTableBanner);
}

const localSelectedTableType = localStorage.getItem('selected-table-type');
if (localSelectedTableType) {
  // because of legacy data, we need to remove quotes from the string if they exist
  initialState.selectedTableType = localSelectedTableType.replace(/['"]+/g, '') as TableType;
}

const reducer = produce((draft: IInsightsContext, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case INSIGHTS_CONTEXT_KEYS.SELECTED_TABLE_TYPE:
      localStorage.setItem('selected-table-type', payload as string);
      draft.selectedTableType = payload as TableType;
      break;

    case INSIGHTS_CONTEXT_KEYS.SELECTED_SHOW:
      draft.selectedShow = payload as string;
      if (!payload) {
        draft.selectedGarmCategory = '';
      }
      break;

    case INSIGHTS_CONTEXT_KEYS.SELECTED_GARM_CATEGORY:
      draft.selectedGarmCategory = payload as string;
      break;

    case INSIGHTS_CONTEXT_KEYS.EPISODE_LIST_EXPANDED:
      draft.episodeListExpanded = payload as boolean;
      break;

    case INSIGHTS_CONTEXT_KEYS.SHOULD_SHOW_SHOWS_TABLE_BANNER:
      draft.shouldShowShowsTableBanner = payload as boolean;
      break;

    case INSIGHTS_CONTEXT_KEYS.PERSISTED_SHOULD_SHOW_SHOWS_TABLE_BANNER:
      localStorage.setItem('show-shows-table-banner', JSON.stringify(payload));
      draft.persistedShouldShowShowsTableBanner = payload as boolean | null;
      draft.shouldShowShowsTableBanner = payload as boolean;
      break;

    case INSIGHTS_CONTEXT_KEYS.SELECTED_CONTEXTUAL_CATEGORY:
      draft.selectedContextualCategory = payload as string;
      break;

    case INSIGHTS_CONTEXT_KEYS.GARM_OVERVIEW_HEIGHT:
      draft.garmOverviewHeight = payload as number;
      break;

    case INSIGHTS_CONTEXT_KEYS.SHOWS_SORT:
      draft.showsSort = payload as ShowsSortType;
      break;
    case INSIGHTS_CONTEXT_KEYS.EPISODES_SORT:
      draft.episodesSort = payload as EpisodeSortType;
      break;

    case INSIGHTS_CONTEXT_KEYS.SELECTED_EPISODE:
      draft.selectedEpisode = payload as string;
      break;

    case INSIGHTS_CONTEXT_KEYS.SHOW_EXPORT_TRANSCRIPT_MODAL:
      draft.showExportTranscriptModal = payload as boolean;
      break;

    case INSIGHTS_CONTEXT_KEYS.IS_TRANSCRIPT_DOWNLOAD_IN_PROGRESS:
      draft.isTranscriptDownloadInProgress = payload as boolean;
      break;

    case INSIGHTS_CONTEXT_KEYS.EPISODE_SEARCH_TERM:
      draft.episodeSearchTerm = payload as string;
      break;

    case INSIGHTS_CONTEXT_KEYS.SHOULD_SHOW_AI_CHAT:
      draft.shouldShowAIChat = payload as boolean;
      break;

    case INSIGHTS_CONTEXT_KEYS.SHOW_FILTERS_MODAL:
      draft.showFiltersModal = payload as boolean;
      break;

    default:
      break;
  }
}, initialState);

export const InsightsContext = createContext<IInsightsContext>(initialState);

export const InsightsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isEnabled: isEpisodeDetailsHidden } = useFeatureFlag(FEATURE_FLAG.HIDE_EPISODE_DETAILS);

  function setSelectedTableType(value: TableType) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SELECTED_TABLE_TYPE, payload: value });
  }

  function setSelectedShow(showId: string) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SELECTED_SHOW, payload: showId });
  }

  function setSelectedGarmCategory(garmCategory: string) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SELECTED_GARM_CATEGORY, payload: garmCategory });
  }

  function setEpisodeListExpanded(value: boolean | ((prevState: boolean) => boolean)) {
    if (typeof value === 'function') {
      value = value(state.episodeListExpanded);
    }
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.EPISODE_LIST_EXPANDED, payload: value });
  }

  function setShouldShowShowsTableBanner(value: boolean) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SHOULD_SHOW_SHOWS_TABLE_BANNER, payload: value });
  }

  function setPersistedShouldShowShowsTableBanner(value: boolean) {
    dispatch({
      type: INSIGHTS_CONTEXT_KEYS.PERSISTED_SHOULD_SHOW_SHOWS_TABLE_BANNER,
      payload: value,
    });
  }

  function setSelectedContextualCategory(value: string) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SELECTED_CONTEXTUAL_CATEGORY, payload: value });
  }

  function setGarmOverviewHeight(value: number) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.GARM_OVERVIEW_HEIGHT, payload: value });
  }

  function setShowsSort(payload: ShowsSortType) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SHOWS_SORT, payload });
  }

  function setEpisodesSort(payload: EpisodeSortType) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.EPISODES_SORT, payload });
  }

  function setSelectedEpisode(payload: string) {
    dispatch({
      type: INSIGHTS_CONTEXT_KEYS.SELECTED_EPISODE,
      payload: isEpisodeDetailsHidden ? '' : payload,
    });
  }

  function setShowExportTranscriptModal(payload: boolean) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SHOW_EXPORT_TRANSCRIPT_MODAL, payload });
  }

  function setIsTranscriptDownloadInProgress(payload: boolean) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.IS_TRANSCRIPT_DOWNLOAD_IN_PROGRESS, payload });
  }

  function setEpisodeSearchTerm(payload: string) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.EPISODE_SEARCH_TERM, payload });
  }

  function setShouldShowAIChat(payload: boolean) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SHOULD_SHOW_AI_CHAT, payload });
  }

  function setShowFiltersModal(payload: boolean) {
    dispatch({ type: INSIGHTS_CONTEXT_KEYS.SHOW_FILTERS_MODAL, payload });
  }

  const value = {
    ...state,
    setSelectedTableType,
    setSelectedShow,
    setSelectedGarmCategory,
    setEpisodeListExpanded,
    setShouldShowShowsTableBanner,
    setPersistedShouldShowShowsTableBanner,
    setSelectedContextualCategory,
    setGarmOverviewHeight,
    setShowsSort,
    setEpisodesSort,
    setSelectedEpisode,
    setShowExportTranscriptModal,
    setIsTranscriptDownloadInProgress,
    setEpisodeSearchTerm,
    setShouldShowAIChat,
    setShowFiltersModal,
  };

  return <InsightsContext.Provider value={value}>{children}</InsightsContext.Provider>;
};

export const useInsightsContext = () => {
  return useContext(InsightsContext);
};
